import { useEffect, useMemo, useState, useRef } from "react";
import { Center, Flex, Loader, RangeSlider, Text, Title, Tooltip, ActionIcon, useMantineTheme } from "@mantine/core";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_SortingState,
  type MRT_ColumnFiltersState,
} from "mantine-react-table";
import { IconEye, IconInfoCircle } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useDebouncedValue } from "@mantine/hooks";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { AdminStatusBadge, UCC_STATUS_OPTIONS } from "@/pages/admin/AdminStatusBadge";
import { formatDate } from "@/shared/utils/format.utils";
import { enumLabel, GENDER_LABEL, OCCUPATION_LABEL, INCOME_SLAB_LABEL } from "@/shared/constants/general.const";

type UserListItem = {
  id: string;
  name: string;
  email: string;
  uccNumber: string | null;
  mobileNumber: string | null;
  countryCode: string | null;
  age: number;
  investmentAccountStatus: string | null;
  goalsCreated: number;
  dateOfBirth: string;
  gender: string | null;
  panNumber: string | null;
  investorType: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  placeOfBirth: string | null;
  occupation: string | null;
  incomeSlab: string | null;
  referralCode: string | null;
  referralCount: number;
  createdAt: string;
  updatedAt: string;
};

const PAGE_SIZE = 10;
const AGE_MIN = 18;
const AGE_MAX = 100;

const GENDER_OPTIONS = Object.entries(GENDER_LABEL).map(([value, label]) => ({ value, label }));
const OCCUPATION_OPTIONS = Object.entries(OCCUPATION_LABEL).map(([value, label]) => ({ value, label }));
const INCOME_SLAB_OPTIONS = Object.entries(INCOME_SLAB_LABEL).map(([value, label]) => ({ value, label }));

export default function UsersPage() {
  const navigate = useNavigate();
  const theme = useMantineTheme();

  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [registeredUsers, setRegisteredUsers] = useState(0);
  const [activeUsers, setActiveUsers] = useState(0);

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [debouncedFilters] = useDebouncedValue(columnFilters, 400);

  const previousParamsRef = useRef<string>("");

  const filterParams = useMemo(() => {
    const active = debouncedFilters.filter((f) => f.value !== undefined && f.value !== null && f.value !== "");
    return Object.fromEntries(active.map((f) => [f.id, f.value]));
  }, [debouncedFilters]);

  useEffect(() => {
    const params = {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      sorting: sorting.map((s) => ({ id: s.id, desc: s.desc })),
    };

    const paramsString = JSON.stringify({ ...params, filterParams });
    if (paramsString === previousParamsRef.current) return;
    previousParamsRef.current = paramsString;

    const fetchUsers = async () => {
      setLoading(true);
      try {
        const filter: Record<string, any> = {};
        if (filterParams.name) filter.name = filterParams.name;
        if (filterParams.email) filter.email = filterParams.email;
        if (filterParams.mobileNumber) filter.mobileNumber = filterParams.mobileNumber;
        if (filterParams.investmentAccountStatus) filter.investmentAccountStatus = filterParams.investmentAccountStatus;
        if (Array.isArray(filterParams.age)) {
          const [minAge, maxAge] = filterParams.age as [number, number];
          filter.minAge = minAge;
          filter.maxAge = maxAge;
        }
        if (filterParams.panNumber) filter.panNumber = filterParams.panNumber;
        if (filterParams.city) filter.city = filterParams.city;
        if (filterParams.state) filter.state = filterParams.state;
        if (filterParams.country) filter.country = filterParams.country;
        if (filterParams.placeOfBirth) filter.placeOfBirth = filterParams.placeOfBirth;
        if (filterParams.referralCode) filter.referralCode = filterParams.referralCode;
        if (filterParams.gender) filter.gender = filterParams.gender;
        if (filterParams.occupation) filter.occupation = filterParams.occupation;
        if (filterParams.incomeSlab) filter.incomeSlab = filterParams.incomeSlab;
        if (Array.isArray(filterParams.referralCount)) {
          const [min, max] = filterParams.referralCount;
          if (min !== "" && min != null) filter.minReferralCount = Number(min);
          if (max !== "" && max != null) filter.maxReferralCount = Number(max);
        }
        if (Array.isArray(filterParams.dateOfBirth)) {
          const [from, to] = filterParams.dateOfBirth as [Date | null, Date | null];
          if (from) filter.dateOfBirthFrom = from;
          if (to) filter.dateOfBirthTo = to;
        }
        if (Array.isArray(filterParams.createdAt)) {
          const [from, to] = filterParams.createdAt as [Date | null, Date | null];
          if (from) filter.createdAtFrom = from;
          if (to) filter.createdAtTo = to;
        }
        if (Array.isArray(filterParams.updatedAt)) {
          const [from, to] = filterParams.updatedAt as [Date | null, Date | null];
          if (from) filter.updatedAtFrom = from;
          if (to) filter.updatedAtTo = to;
        }

        const res = await trpc.users.list.query({
          page: params.page,
          limit: params.limit,
          filter: Object.keys(filter).length > 0 ? filter : undefined,
          sorting: params.sorting.length > 0 ? params.sorting : undefined,
        });

        setUsers(res.data.users);
        setTotalRows(res.data.meta.total);
        setRegisteredUsers(res.data.registeredUsers);
        setActiveUsers(res.data.activeUsers);
      } catch (err) {
        handleTrpcError(err);
        setUsers([]);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [pagination.pageIndex, pagination.pageSize, filterParams, sorting]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (pagination.pageIndex !== 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [filterParams]);

  const columns: MRT_ColumnDef<UserListItem>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 180,
        enableSorting: true,
        Cell: ({ cell }) => cell.getValue<string>() || "—",
      },
      {
        accessorKey: "email",
        header: "Email ID",
        size: 200,
        enableSorting: true,
      },
      {
        accessorKey: "uccNumber",
        header: "UCC Number",
        size: 150,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) =>
          cell.getValue<string | null>() ?? <span style={{ color: "var(--mantine-color-slate-4)" }}>—</span>,
      },
      {
        accessorKey: "investmentAccountStatus",
        header: "Investment Acc Status",
        size: 190,
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: UCC_STATUS_OPTIONS,
          clearable: true,
        },
        enableSorting: false,
        Cell: ({ cell }) => <AdminStatusBadge status={cell.getValue<string | null>()} />,
      },
      {
        accessorKey: "mobileNumber",
        header: "Mobile No",
        size: 150,
        enableSorting: false,
        Cell: ({ cell, row }) => {
          const mobile = cell.getValue<string | null>();
          if (!mobile) return <span style={{ color: "var(--mantine-color-slate-4)" }}>—</span>;
          return `${row.original.countryCode ?? ""} ${mobile}`.trim();
        },
      },
      {
        accessorKey: "age",
        header: "Age",
        size: 200,
        enableSorting: true,

        Filter: ({ column }) => {
          const value = column.getFilterValue() as [number, number] | undefined;
          const [min, max] = value ?? [AGE_MIN, AGE_MAX];
          return (
            <RangeSlider
              min={AGE_MIN}
              max={AGE_MAX}
              value={[min, max]}
              onChangeEnd={(val) => column.setFilterValue(val[0] === AGE_MIN && val[1] === AGE_MAX ? undefined : val)}
              minRange={1}
              size="sm"
              label={(v) => String(v)}
              mt={12}
              style={{ width: "80%" }}
            />
          );
        },
      },
      {
        accessorKey: "goalsCreated",
        header: "No of Goal Created",
        size: 160,
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        accessorKey: "dateOfBirth",
        header: "Date of Birth",
        size: 220,
        filterVariant: "date-range",
        enableSorting: false,
        Cell: ({ cell }) => formatDate(cell.getValue<string>()),
      },
      {
        accessorKey: "gender",
        header: "Gender",
        size: 140,
        filterVariant: "select",
        mantineFilterSelectProps: { data: GENDER_OPTIONS, clearable: true },
        enableSorting: false,
        Cell: ({ cell }) => enumLabel(GENDER_LABEL, cell.getValue<string | null>()) ?? "—",
      },
      {
        accessorKey: "panNumber",
        header: "PAN",
        size: 160,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "investorType",
        header: "Investor Type",
        size: 140,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "occupation",
        header: "Occupation",
        size: 200,
        filterVariant: "select",
        mantineFilterSelectProps: { data: OCCUPATION_OPTIONS, clearable: true },
        enableSorting: false,
        Cell: ({ cell }) => enumLabel(OCCUPATION_LABEL, cell.getValue<string | null>()) ?? "—",
      },
      {
        accessorKey: "incomeSlab",
        header: "Income Slab",
        size: 200,
        filterVariant: "select",
        mantineFilterSelectProps: { data: INCOME_SLAB_OPTIONS, clearable: true },
        enableSorting: false,
        Cell: ({ cell }) => enumLabel(INCOME_SLAB_LABEL, cell.getValue<string | null>()) ?? "—",
      },
      {
        accessorKey: "city",
        header: "City",
        size: 130,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "state",
        header: "State",
        size: 130,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "country",
        header: "Country",
        size: 100,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "placeOfBirth",
        header: "Place of Birth",
        size: 160,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "referralCode",
        header: "Referral Code",
        size: 150,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "referralCount",
        header: "Referral Count",
        size: 180,
        filterVariant: "range",
        enableSorting: true,
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        size: 220,
        filterVariant: "date-range",
        enableSorting: true,
        Cell: ({ cell }) => formatDate(cell.getValue<string>()),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated At",
        size: 220,
        filterVariant: "date-range",
        enableSorting: true,
        Cell: ({ cell }) => formatDate(cell.getValue<string>()),
      },
    ],
    []
  );

  if (loading && users.length === 0) {
    return (
      <Center h="100vh">
        <Loader size="lg" type="dots" />
      </Center>
    );
  }

  return (
    <Flex direction="column" w="100%" p="xl">
      {/* Page header with stats */}
      <Flex justify="space-between" align="center" mb="xl">
        <Title order={2} fw={700}>
          All Users
        </Title>
        <Flex gap="sm">
          <Flex
            align="center"
            justify="space-between"
            gap="md"
            px="md"
            py="sm"
            mih={44}
            style={{
              minWidth: 252,
              backgroundColor: "#F5F2FE",
              borderRadius: "10px",
            }}
          >
            <Flex align="center" gap={6}>
              <Text fz="sm" c="dark.6">
                Registered User
              </Text>
              <Tooltip label="Total users registered on the platform">
                <IconInfoCircle size={14} color="var(--mantine-color-gray-6)" style={{ cursor: "default" }} />
              </Tooltip>
            </Flex>
            <Text fz="xl" fw={700} c="#6F58B0">
              {registeredUsers}
            </Text>
          </Flex>

          <Flex
            align="center"
            justify="space-between"
            gap="md"
            px="md"
            py="sm"
            mih={44}
            style={{
              minWidth: 252,
              backgroundColor: "#F5F2FE",
              borderRadius: "10px",
            }}
          >
            <Flex align="center" gap={6}>
              <Text fz="sm" c="dark.6">
                Active users
              </Text>
              <Tooltip label="Users with an active investment account">
                <IconInfoCircle size={14} color="var(--mantine-color-gray-6)" style={{ cursor: "default" }} />
              </Tooltip>
            </Flex>
            <Text fz="xl" fw={700} c="#6F58B0">
              {activeUsers}
            </Text>
          </Flex>
        </Flex>
      </Flex>

      <MantineReactTable
        columns={columns}
        data={users}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableRowActions
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        manualPagination
        manualFiltering
        manualSorting
        enableGlobalFilter={false}
        positionToolbarAlertBanner="none"
        rowCount={totalRows}
        onPaginationChange={setPagination}
        onColumnFiltersChange={setColumnFilters}
        onSortingChange={setSorting}
        state={{
          pagination,
          columnFilters,
          sorting,
          isLoading: loading,
        }}
        positionActionsColumn="last"
        mantinePaginationProps={{
          rowsPerPageOptions: ["10", "25", "50", "100"],
        }}
        paginationDisplayMode="pages"
        renderRowActions={({ row }) => (
          <Tooltip label="View Details">
            <ActionIcon c="slate.6" variant="transparent" onClick={() => navigate(`/users/${row.original.id}`)}>
              <IconEye size={18} />
            </ActionIcon>
          </Tooltip>
        )}
        initialState={{
          columnPinning: { right: ["mrt-row-actions"] },
          density: "xs",
        }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            borderRight: "1px solid var(--mantine-color-gray-2)",
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 400,
            fontSize: "var(--mantine-font-size-sm)",
            color: theme.colors.slate[5],
            borderRight: "1px solid var(--mantine-color-gray-2)",
            backgroundColor: "var(--mantine-color-gray-0)",
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "calc(100vh - 250px)",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
        renderEmptyRowsFallback={() => (
          <tr>
            <td colSpan={columns.length + 2} style={{ textAlign: "center", padding: "2rem" }}>
              No users found. Try adjusting your filters.
            </td>
          </tr>
        )}
      />
    </Flex>
  );
}
